import { Box, sanitizeToNumeric } from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import usePostCreateWorkflow from '@/hooks/useApi/usePostCreateWorkflow';
import useDeleteWorkflow from '@/hooks/useApi/useDeleteWorkflow';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import CreateWorkflowForm from './CreateWorkflowForm';
import type { UpdateWorkflowContainer } from './types';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { Trans, useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { Title, Button, UtilButton } from 'UI';
import { useDataFetchIndicator } from '@/hooks';
import { useDialog } from '@/providers/Dialog.provider';
import WorkflowCreatedDialog from './WorkflowCreatedDialog';
import SubAccountsWorkflowDialog from './SubAccountsWorkflowDialog';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import styles from './WorkflowContainer.module.scss';
import { propagateToSubAccounts } from './propagateToSubAccounts';

type FC = FunctionComponent;

const CreateWorkflowContainer: FC = () => {
  const history = useHistory();
  const dialog = useDialog();
  const [isReview, setIsReview] = useState(false);
  const { workflows, fetchWorkflows } = useFetchWorkflows();
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [workflow, setWorkflow] = useState<UpdateWorkflowContainer | undefined>(undefined);
  const [, , createWorkFlowAsync] = usePostCreateWorkflow({});
  const [, , deleteWorkflowAsync] = useDeleteWorkflow({});
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const { approvalListReset, transactionsReset } = useApprovalWorkflow();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows.Update' });
  const alertDialog = useAlertDialog();
  const toast = useToast();

  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const {
    response: { Country },
  } = useClientData();

  useEffect(() => {
    fetchWorkflows().catch(() => alertDialog.error(tGlobal('error'), tGlobal('errorOccurred')));
    fetchTransactionTypes({
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
  }, []);

  const createWorkflow = async () => {
    if (!workflow?.values) return;
    const { TransactionTypes, tiers, applyToSubAccounts } = workflow.values;
    const filteredTiers = tiers?.filter((tier) => tier.approversNeeded !== '') || [];

    try {
      for (const transactionType of TransactionTypes) {
        for (const tier of filteredTiers) {
          const res = await createWorkFlowAsync({
            TransactionType: transactionType,
            NumberApprovalsRequired: sanitizeToNumeric(tier.approversNeeded) || '0',
            MinAmount: sanitizeToNumeric(tier.startAmount || '0') || '0',
            MaxAmount: tier.noUpperLimit ? null : sanitizeToNumeric(tier.endAmount),
            ApplyToAllSubaccounts: applyToSubAccounts,
          });
          if (!res.Success) throw Error(res.ErrorMessage);
        }
      }

      let resp: boolean | null | undefined;

      if (applyToSubAccounts) {
        const { perRail, failedPermission, totalSubAccounts } = await propagateToSubAccounts(
          TransactionTypes,
          filteredTiers,
          { setDataFetchingInfo, fetchSubAccountsAsync, fetchWorkflows, createWorkFlowAsync, deleteWorkflowAsync },
        );

        resp = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
          <SubAccountsWorkflowDialog
            perRail={perRail}
            failedPermission={failedPermission}
            totalSubAccounts={totalSubAccounts}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        ));
      } else {
        toast.success(
          <Trans
            i18nKey="TransactionApproval.Configuration.Workflows.Update.successfullyCreatedToast"
            ns="translations"
            defaultValue="<0>Workflow Create</0> The workflow has been created successfully."
            components={[<span className={styles.bold} />]}
          />,
        );
        resp = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
          <WorkflowCreatedDialog onConfirm={onConfirm} onCancel={onCancel} />
        ));
      }

      transactionsReset();
      approvalListReset();
      if (resp) {
        history.push('/transaction-approval/configuration/roles');
      } else {
        history.push('/transaction-approval/configuration/workflows');
      }
    } catch (err) {
      fetchWorkflows();
      alertDialog.error(tGlobal('error'), err instanceof Error ? err.message : tGlobal('errorOccurred'));
    }
  };

  return (
    <div className={styles.page}>
      <Box flx="col" gapSize="16px">
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace('/transaction-approval/configuration/workflows');
            }
          }}
        >
          {history.length > 1 ? tGlobal('Buttons.back') : t('backButton')}
        </UtilButton>

        <Title level="1">
          {isReview
            ? t('reviewTitle', { defaultValue: 'Review Workflow' })
            : t('createTitle', { defaultValue: 'Create Workflow' })}
        </Title>
      </Box>
      <div className={styles.formWrapper}>
        <CreateWorkflowForm
          defaultValues={workflow?.values}
          isReview={isReview}
          onChange={(values, isValid) => setWorkflow({ values, isValid })}
          workflows={workflows}
          transactionTypesResponse={transactionTypesResponse}
        />
      </div>
      <Box jc="fe">
        <Box gapSize="12px">
          {isReview && (
            <Button
              id="approval-edit-review"
              colorScheme="secondary"
              iconLeftProps={{ icon: 'edit', fillAmount: 1 }}
              onClick={() => setIsReview(false)}
            >
              {t('Buttons.edit', { defaultValue: 'Edit' })}
            </Button>
          )}
          <Button
            id="approval-create-workflow"
            disabled={!workflow?.isValid}
            colorScheme="primary"
            onClick={() => {
              if (!isReview) {
                setIsReview(true);
              } else {
                createWorkflow();
              }
            }}
          >
            {isReview
              ? t('Buttons.createWorkflow', { defaultValue: 'Create Workflow' })
              : t('Buttons.review', { defaultValue: 'Review' })}
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default CreateWorkflowContainer;
